import '../base';

const ATTR_SECTION_INITIALIZED = 'data-section-initialized';

type ExtendsSection<E extends HTMLElement, J extends Section<E>> = (
  new(element:E, selector:string) => J
);
export abstract class Section<T extends HTMLElement = HTMLElement> {
  private static registrations:{ [key:string]:ExtendsSection<any,any> } = {};

  /**
   * Register a given section handler for a section selector.
   * 
   * @param selector Selector used to query on the section(s).
   * @param t The handling class for that section.
   * @returns The list of currently registered sections.
   */
  public static register<
    E extends HTMLElement,J extends Section<E>
  >(selector:string, t:ExtendsSection<E,J>):J[] {
    const sections:J[] = [];
    document.querySelectorAll<E>(selector).forEach(element => {
      if(Section.isRegistered(element)) return;
      const sect = new t(element, selector);
      sections.push(sect);

      // First, query children
      sect.queryChildren(element);

      // Then do any init that it may need to do
      sect.init();

      // Finally attach events.
      sect.attachEvents(element);
    });
    this.registrations[selector] = t;
    return sections;
  }

  public static recheck<
    E extends HTMLElement,J extends Section<E>
  >(selector:string):J[] {
    const t = this.registrations[selector];
    if(!t) return [];
    return Section.register<E,J>(selector, t);
  }

  /**
   * Checks if the given section has been registered or not.
   * 
   * @param element Element to check if has been registered.
   * @returns True if registered, otherwise false.
   */
  public static isRegistered(element:HTMLElement) {
    return element.hasAttribute(ATTR_SECTION_INITIALIZED);
  }

  // Class itself.
  private readonly selector:string;
  protected container:T;
  protected shopifyContainer:HTMLDivElement|null;
  protected id?:string;

  private readonly innerHtmlObserver:MutationObserver|null;

  public constructor(element:T, selector:string) {
    this.container = element;
    this.selector = selector;
    this.container.setAttribute(ATTR_SECTION_INITIALIZED, 'true');

    // Query on Shopify's container
    this.shopifyContainer = this.container.parentElement as HTMLDivElement|null;
    if(this.shopifyContainer && this.shopifyContainer.id) {
      // Try extract the ID
      try {
        this.id = this.shopifyContainer.id.replace(/shopify-section-/, '');
      } catch(e) {
        console.warn(e);
      }
    }

    // Mutation observer in dev env. This watches for HMR reloads of a section.
    if(process.env.NODE_ENV !== 'production') {
      if(this.shopifyContainer && this.shopifyContainer.parentElement) {
        this.innerHtmlObserver = new MutationObserver(this.onInnerHtmlMutationObserved);
        this.innerHtmlObserver.observe(this.shopifyContainer.parentElement, {
          subtree: false, childList: true
        });
      } else {
        this.innerHtmlObserver = null;
      }
    } else {
      this.innerHtmlObserver = null;
    }
  }
  
  protected replaceContainer(newContainer:T) {
    this.container.removeAttribute(ATTR_SECTION_INITIALIZED);
    this.detatchEvents(this.container);
    this.container.innerHTML = newContainer.innerHTML;
    this.container.setAttribute(ATTR_SECTION_INITIALIZED, 'true');
    this.queryChildren(this.container);
    this.init();
    this.attachEvents(this.container);
  }

  abstract queryChildren(container:T):void;
  protected init():void {}
  abstract attachEvents(container:T):void;
  abstract detatchEvents(container:T):void;

  onInnerHtmlMutationObserved:MutationCallback = (mutations, observer) => {
    if(!mutations.length) return;
    if(!this.shopifyContainer) return;
    const [ mutation ] = mutations;
    const removedNodes:HTMLDivElement[] = [];
    mutation.removedNodes.forEach(n => removedNodes.push(n as HTMLDivElement));
    const index = removedNodes.findIndex(rn => rn === this.shopifyContainer);
    if(index === -1) return;
    this.dispose();
    Section.recheck<T, typeof this>(this.selector);
  }

  public dispose():void {
    this.container.removeAttribute(ATTR_SECTION_INITIALIZED);
    this.detatchEvents(this.container);
    if(this.innerHtmlObserver) this.innerHtmlObserver.disconnect();
  }
}